import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { Braces, Plus, Trash2 } from 'lucide-react'
import { useRef, useState, type ReactNode, type Ref } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import {
  renderTemplate,
  templateSlotLabels,
  templateSlots,
  type TemplateButton,
  type TemplateButtonType,
  type TemplateStructure,
  type TemplateVariable,
} from '@aeris/communication'
import {
  Button,
  ConfirmDialog,
  Field,
  Input,
  Page,
  PageHeader,
  RadioGroup,
  Select,
  Textarea,
} from '@aeris/ui'

import { WhatsAppPreview } from '../components/whatsapp-preview.tsx'
import { ApiError, createTemplate, fetchChannelAccounts } from '../lib/api.ts'

/**
 * Atendimento → Modelos → Novo: o formulário que cria um modelo (F059).
 *
 * **Página, não modal.** O pedido do dono foi literal: quem decide como o
 * cliente vai LER a mensagem — negrito, botão, variável — precisa ver a
 * mensagem enquanto escreve, e uma caixa de diálogo de largura fixa não tem
 * espaço para isso. `grid gap-4 lg:grid-cols-[1fr_22rem]` — formulário à
 * esquerda, `WhatsAppPreview` fixa à direita — é a resposta.
 *
 * A prévia é `renderTemplate` (`@aeris/communication`) sobre o texto e as
 * variáveis JÁ digitados, renderizado por `WhatsAppPreview`
 * (`../components/whatsapp-preview.tsx`) — nunca uma segunda leitura do
 * texto feita por este arquivo. Ver a doutrina em `receipt-dialog.tsx`
 * (linhas 22-39): o documento é dado, a UI só mostra.
 *
 * **Ninguém digita `{{1}}` na mão.** "Inserir variável" grava o token na
 * posição do cursor e acrescenta uma entrada à lista de variáveis, cada uma
 * com NOME (o que aparece na prévia como `title` do realce) e EXEMPLO (o
 * valor que a prévia usa para simular o preenchimento). Remover uma
 * variável reindexa `1..n` no texto inteiro (cabeçalho, corpo e rodapé) E na
 * lista, no mesmo passo — nunca um buraco na numeração.
 *
 * `watch()` do react-hook-form não é usado — quebra o React Compiler (ver
 * `party-form.tsx:140-160`). O que a prévia precisa (cabeçalho/corpo/rodapé)
 * é espelhado em `useState` via `onChange` de `register`.
 *
 * ## F066 — o que mudou, e o que continua bloqueado
 *
 * **A estrutura é montada UMA vez.** Antes desta feature havia duas
 * construções (`structureFrom(mirror)` para a prévia e um segundo literal
 * dentro de `save.mutationFn`), com `trim()` em pontos diferentes: a prévia
 * podia discordar do que era submetido, que é exatamente o defeito que o
 * topo deste arquivo diz querer evitar. Agora `structureFrom` é a única, e o
 * que é submetido é o objeto que a prévia acabou de desenhar.
 *
 * **Botões.** `WhatsAppPreview` já desenhava `QUICK_REPLY`/`URL`/
 * `PHONE_NUMBER` e o contrato já os modelava — mas o formulário mandava
 * `buttons: []` fixo. A prévia conseguia desenhar algo que o formulário não
 * conseguia criar. O editor abaixo fecha isso.
 *
 * **Cabeçalho de mídia: bloqueado no gateway, não esquecido na tela.** O
 * motivo aparece ESCRITO logo abaixo do campo de cabeçalho, com o caminho
 * alternativo — a pergunta do dono foi literalmente "onde põe as imagens
 * aqui?", e uma tela que não responde nada é o pior dos dois mundos. Um
 * campo desabilitado seria pior ainda: um alvo morto que não explica.
 * Criar um modelo com cabeçalho de mídia exige um `header_handle`
 * da Resumable Upload API da Meta, e o OpenBSP não expõe rota de upload
 * (`whatsapp-management` só faz templates e signup; o único upload que ele
 * faz é dentro do dispatcher, para `content.type === 'file'`, que é
 * mensagem comum e não componente de template). Ver o bloqueio arquitetural
 * completo em `docs/features/F066-template-media.md`.
 */

const LANGUAGE_CODES = [
  'pt_BR',
  'pt_PT',
  'en_US',
  'es_ES',
  'es_MX',
  'es_AR',
] as const

const LANGUAGE_LABELS: Record<(typeof LANGUAGE_CODES)[number], string> = {
  pt_BR: 'Português (Brasil)',
  pt_PT: 'Português (Portugal)',
  en_US: 'Inglês (Estados Unidos)',
  es_ES: 'Espanhol (Espanha)',
  es_MX: 'Espanhol (México)',
  es_AR: 'Espanhol (Argentina)',
}

/**
 * O TÍTULO em português vira um identificador no formato que a Meta exige —
 * minúsculo, sem acento, sem espaço. Só a PRIMEIRA derivação: depois que a
 * pessoa edita o identificador à mão, `slugTouched` para de sobrescrevê-lo.
 */
const slugify = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 512)

const formSchema = z.object({
  accountId: z.string().min(1, 'Escolha a conta'),
  title: z.string().trim().min(1, 'Dê um nome a este modelo').max(200),
  metaTemplateName: z
    .string()
    .trim()
    .min(1, 'Informe o identificador')
    .regex(
      /^[a-z0-9_]+$/,
      'Só letras minúsculas, números e sublinhado — a convenção da Meta.'
    ),
  language: z.enum(LANGUAGE_CODES),
  category: z.enum(['MARKETING', 'UTILITY', 'AUTHENTICATION']),
  headerText: z.string().trim().max(60),
  bodyText: z.string().trim().min(1, 'Escreva o texto do modelo'),
  footerText: z.string().trim().max(60),
})

type FormValues = z.infer<typeof formSchema>

type TextMirror = {
  headerText: string
  bodyText: string
  footerText: string
}

const CATEGORY_OPTIONS = [
  {
    value: 'UTILITY',
    label: 'Utilidade',
    description: 'Pedido, entrega, cobrança — algo que o cliente pediu.',
  },
  {
    value: 'MARKETING',
    label: 'Marketing',
    description: 'Promoção, novidade, convite. Custa mais.',
  },
  {
    value: 'AUTHENTICATION',
    label: 'Autenticação',
    description: 'Só código de verificação.',
  },
] as const

const EMPTY_MIRROR: TextMirror = {
  headerText: '',
  bodyText: '',
  footerText: '',
}

/** Reescreve `{{n}}` num texto: apaga o índice removido, renumera os demais pelo mapeamento antigo→novo. */
const rewriteVariableTokens = (
  text: string,
  removedIndex: number,
  mapping: ReadonlyMap<number, number>
): string =>
  text.replace(/\{\{(\d+)\}\}/g, (whole, digits: string) => {
    const oldIndex = Number(digits)
    if (oldIndex === removedIndex) return ''
    const newIndex = mapping.get(oldIndex)
    return newIndex === undefined ? whole : `{{${String(newIndex)}}}`
  })

/** Une a `ref` que `register` devolve com uma `ref` local — para ler `selectionStart` sem abrir mão do registro do react-hook-form. */
const mergeTextareaRefs =
  (a: Ref<HTMLTextAreaElement>, b: Ref<HTMLTextAreaElement>) =>
  (node: HTMLTextAreaElement | null): void => {
    if (typeof a === 'function') a(node)
    else if (a !== null)
      (a as { current: HTMLTextAreaElement | null }).current = node
    if (typeof b === 'function') b(node)
    else if (b !== null)
      (b as { current: HTMLTextAreaElement | null }).current = node
  }

/**
 * A ÚNICA construção da estrutura — a prévia e a submissão leem esta função,
 * nunca duas versões que possam divergir por um `trim()` a mais.
 *
 * O corpo não é `trim()`ado enquanto se digita (senão o cursor pula ao
 * apagar um espaço no fim), então quem submete passa `finalize: true` para
 * aparar as pontas de uma vez. É a mesma estrutura nos dois casos.
 */
const structureFrom = (input: {
  readonly mirror: TextMirror
  readonly buttons: readonly TemplateButton[]
  readonly finalize?: boolean
}): TemplateStructure => {
  const body =
    input.finalize === true
      ? input.mirror.bodyText.trim()
      : input.mirror.bodyText

  return {
    components: [
      ...(input.mirror.headerText.trim() === ''
        ? []
        : [
            {
              type: 'HEADER' as const,
              // Só `TEXT` por enquanto — ver o bloqueio no topo do arquivo.
              format: 'TEXT' as const,
              text: input.mirror.headerText.trim(),
              buttons: [],
            },
          ]),
      { type: 'BODY' as const, text: body, buttons: [] },
      ...(input.mirror.footerText.trim() === ''
        ? []
        : [
            {
              type: 'FOOTER' as const,
              text: input.mirror.footerText.trim(),
              buttons: [],
            },
          ]),
      ...(input.buttons.length === 0
        ? []
        : [
            {
              type: 'BUTTONS' as const,
              text: null,
              buttons: input.buttons,
            },
          ]),
    ],
  }
}

/** Um botão ainda EM EDIÇÃO — `url`/`phoneNumber` existem mesmo quando o tipo não os usa, para que trocar o tipo não apague o que já foi digitado. */
type ButtonDraft = {
  readonly type: TemplateButtonType
  readonly text: string
  readonly url: string
  readonly phoneNumber: string
}

const EMPTY_BUTTON: ButtonDraft = {
  type: 'QUICK_REPLY',
  text: '',
  url: '',
  phoneNumber: '',
}

const BUTTON_TYPE_LABEL: Record<TemplateButtonType, string> = {
  QUICK_REPLY: 'Resposta rápida',
  URL: 'Abrir link',
  PHONE_NUMBER: 'Ligar',
}

/**
 * Converte o rascunho no botão do contrato — `null` quando ainda falta o que
 * aquele tipo exige. A prévia usa o mesmo caminho, então um botão pela metade
 * simplesmente não aparece nela, em vez de aparecer torto.
 */
const buttonOf = (draft: ButtonDraft): TemplateButton | null => {
  const text = draft.text.trim()
  if (text === '') return null
  if (draft.type === 'QUICK_REPLY') return { type: 'QUICK_REPLY', text }
  if (draft.type === 'URL') {
    const url = draft.url.trim()
    return url === '' ? null : { type: 'URL', text, url }
  }
  const phoneNumber = draft.phoneNumber.trim()
  return phoneNumber === '' ? null : { type: 'PHONE_NUMBER', text, phoneNumber }
}

export const AtendimentoModelosNovoPage = (): ReactNode => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [formError, setFormError] = useState<string | null>(null)
  const [slugTouched, setSlugTouched] = useState(false)
  const [mirror, setMirror] = useState<TextMirror>(EMPTY_MIRROR)
  const [variables, setVariables] = useState<readonly TemplateVariable[]>([])
  const [buttonDrafts, setButtonDrafts] = useState<readonly ButtonDraft[]>([])
  const [category, setCategory] = useState<FormValues['category']>('UTILITY')
  const [confirming, setConfirming] = useState<FormValues | null>(null)

  const bodyRef = useRef<HTMLTextAreaElement | null>(null)

  /** Os botões PRONTOS — os que a prévia desenha e os que a submissão manda. Um rascunho incompleto fica de fora dos dois. */
  const buttons = buttonDrafts
    .map(buttonOf)
    .filter((button): button is TemplateButton => button !== null)

  const accounts = useQuery({
    queryKey: ['communication-channel-accounts'],
    queryFn: async ({ signal }) => await fetchChannelAccounts(signal),
    // Modelo, campanha e follow-up são da API oficial: o aparelho vinculado
    // não tem modelo e não pode abrir conversa com quem nunca escreveu (F192).
    select: (rows) =>
      rows.filter((account) => account.connection === 'cloud_api'),
  })

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      accountId: '',
      title: '',
      metaTemplateName: '',
      language: 'pt_BR',
      category: 'UTILITY',
      headerText: '',
      bodyText: '',
      footerText: '',
    },
  })

  const titleField = register('title', {
    onChange: (event: { target: { value: string } }) => {
      if (slugTouched) return
      setValue('metaTemplateName', slugify(event.target.value), {
        shouldValidate: true,
      })
    },
  })

  const slugField = register('metaTemplateName', {
    onChange: () => {
      setSlugTouched(true)
    },
  })

  const headerField = register('headerText', {
    onChange: (event: { target: { value: string } }) => {
      setMirror((current) => ({ ...current, headerText: event.target.value }))
    },
  })

  const bodyField = register('bodyText', {
    onChange: (event: { target: { value: string } }) => {
      setMirror((current) => ({ ...current, bodyText: event.target.value }))
    },
  })

  const footerField = register('footerText', {
    onChange: (event: { target: { value: string } }) => {
      setMirror((current) => ({ ...current, footerText: event.target.value }))
    },
  })

  const insertVariable = (): void => {
    const element = bodyRef.current
    const text = element?.value ?? mirror.bodyText
    const start = element?.selectionStart ?? text.length
    const end = element?.selectionEnd ?? text.length
    const nextIndex =
      variables.reduce((max, variable) => Math.max(max, variable.index), 0) + 1
    const token = `{{${String(nextIndex)}}}`
    const nextText = text.slice(0, start) + token + text.slice(end)

    setValue('bodyText', nextText, { shouldDirty: true, shouldValidate: true })
    setMirror((current) => ({ ...current, bodyText: nextText }))
    setVariables((current) => [
      ...current,
      { index: nextIndex, name: '', example: '' },
    ])

    const cursor = start + token.length
    requestAnimationFrame(() => {
      element?.focus()
      element?.setSelectionRange(cursor, cursor)
    })
  }

  const removeVariable = (index: number): void => {
    const remaining = [...variables]
      .filter((variable) => variable.index !== index)
      .sort((a, b) => a.index - b.index)
    const mapping = new Map(
      remaining.map((variable, position) => [variable.index, position + 1])
    )
    const reindexed = remaining.map((variable, position) => ({
      ...variable,
      index: position + 1,
    }))

    const nextMirror: TextMirror = {
      headerText: rewriteVariableTokens(mirror.headerText, index, mapping),
      bodyText: rewriteVariableTokens(mirror.bodyText, index, mapping),
      footerText: rewriteVariableTokens(mirror.footerText, index, mapping),
    }

    setValue('headerText', nextMirror.headerText, { shouldDirty: true })
    setValue('bodyText', nextMirror.bodyText, {
      shouldDirty: true,
      shouldValidate: true,
    })
    setValue('footerText', nextMirror.footerText, { shouldDirty: true })
    setMirror(nextMirror)
    setVariables(reindexed)
  }

  const addButton = (): void => {
    setButtonDrafts((current) => [...current, EMPTY_BUTTON])
  }

  const removeButton = (position: number): void => {
    setButtonDrafts((current) =>
      current.filter((_, index) => index !== position)
    )
  }

  const updateButton = (
    position: number,
    patch: Partial<ButtonDraft>
  ): void => {
    setButtonDrafts((current) =>
      current.map((draft, index) =>
        index === position ? { ...draft, ...patch } : draft
      )
    )
  }

  const renameVariable = (index: number, name: string): void => {
    setVariables((current) =>
      current.map((variable) =>
        variable.index === index ? { ...variable, name } : variable
      )
    )
  }

  const exemplifyVariable = (index: number, example: string): void => {
    setVariables((current) =>
      current.map((variable) =>
        variable.index === index ? { ...variable, example } : variable
      )
    )
  }

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      await createTemplate({
        accountId: values.accountId,
        metaTemplateName: values.metaTemplateName.trim(),
        language: values.language,
        category: values.category,
        // A MESMA função que a prévia acabou de desenhar — nunca uma
        // segunda montagem (ver o topo do arquivo). O `map` só tira o
        // `readonly` que o tipo do domínio impõe e o do contrato não usa.
        structure: {
          components: structureFrom({
            mirror,
            buttons,
            finalize: true,
          }).components.map((component) => ({
            ...component,
            buttons: [...component.buttons],
          })),
        },
        variables: [...variables],
      })
    },
    onSuccess: async () => {
      setFormError(null)
      await queryClient.invalidateQueries({
        queryKey: ['communication-templates'],
      })
      await navigate({ to: '/atendimento-modelos' })
    },
    onError: (cause: unknown) => {
      setFormError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível criar este modelo.'
      )
    },
  })

  /**
   * Submeter é IRREVERSÍVEL e demora dias — a Meta não tem "cancelar
   * análise". Por isso o clique não envia: abre a confirmação, e é ela que
   * chama `save.mutate`. As validações que dependem de estado fora do
   * react-hook-form (variáveis sem nome, botão pela metade) acontecem ANTES
   * de abrir a confirmação, para que a caixa nunca apareça sobre um
   * formulário que ainda vai ser recusado.
   */
  const onSubmit = handleSubmit((values) => {
    setFormError(null)

    const semNome = variables.find((variable) => variable.name.trim() === '')
    if (semNome !== undefined) {
      setFormError(
        `Dê um nome à variável {{${String(semNome.index)}}} antes de salvar.`
      )
      return
    }

    const incompleto = buttonDrafts.findIndex(
      (draft) => buttonOf(draft) === null
    )
    if (incompleto !== -1) {
      setFormError(
        `Complete o botão ${String(incompleto + 1)} — ou remova-o antes de submeter.`
      )
      return
    }

    setConfirming(values)
  })

  const structure = structureFrom({ mirror, buttons })
  const slots = templateSlots(structure)
  const slotLabels = templateSlotLabels({ structure, variables })
  const previewValues = slots.map(
    (slot) =>
      variables.find((variable) => variable.index === slot)?.example ?? ''
  )
  const rendered = renderTemplate({ structure, values: previewValues })

  return (
    <Page>
      <PageHeader
        eyebrow={
          <Link
            to="/atendimento-modelos"
            className="text-(--brand-primary) hover:underline"
          >
            ← Modelos
          </Link>
        }
        title="Novo modelo"
        description="Ele nasce em análise — a aprovação pela Meta leva alguns dias, não minutos. Você não vai poder enviá-lo hoje."
      />

      <div className="grid gap-4 lg:grid-cols-[1fr_22rem]">
        <form
          noValidate
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            void onSubmit(event)
          }}
        >
          <Field label="Conta" required error={errors.accountId?.message}>
            {(field) => (
              <Select
                {...field}
                {...register('accountId')}
                disabled={accounts.isPending}
              >
                <option value="">Escolha uma conta</option>
                {(accounts.data ?? []).map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.displayName}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Nome do modelo"
              required
              error={errors.title?.message}
              hint="Como sua equipe vai reconhecer este modelo."
            >
              {(field) => (
                <Input
                  {...field}
                  {...titleField}
                  placeholder="Confirmação de pedido"
                />
              )}
            </Field>

            <Field
              label="Identificador"
              required
              error={errors.metaTemplateName?.message}
              hint="Gerado a partir do nome — edite se quiser. Minúsculo, sem espaço: a convenção da Meta."
            >
              {(field) => (
                <Input
                  {...field}
                  {...slugField}
                  placeholder="confirmacao_de_pedido"
                  className="font-(--font-mono)"
                />
              )}
            </Field>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Idioma" required error={errors.language?.message}>
              {(field) => (
                <Select {...field} {...register('language')}>
                  {LANGUAGE_CODES.map((code) => (
                    <option key={code} value={code}>
                      {LANGUAGE_LABELS[code]}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <Field
              label="Categoria"
              required
              error={errors.category?.message}
              hint="A categoria decide o custo da mensagem — a Meta pode mudá-la depois de aprovar."
            >
              {(field) => (
                <RadioGroup
                  {...field}
                  value={category}
                  onValueChange={(next) => {
                    setCategory(next)
                    setValue('category', next, { shouldValidate: true })
                  }}
                  options={CATEGORY_OPTIONS}
                />
              )}
            </Field>
          </div>

          <Field
            label="Cabeçalho"
            error={errors.headerText?.message}
            hint="Opcional. Uma linha de texto, sem variáveis."
          >
            {(field) => <Input {...field} {...headerField} />}
          </Field>

          {/*
            F066 — a resposta para "onde põe as imagens aqui?". O bloqueio é
            do gateway, não da tela: ver o comentário no topo do arquivo. Um
            aviso escrito é melhor que um campo ausente (ninguém descobre por
            que não existe) e melhor que um campo que aceita e falha depois.
          */}
          <p className="rounded-(--radius-md) bg-(--warning-subtle) p-3 text-(length:--text-sm) text-(--text-secondary)">
            <strong className="font-(--weight-medium)">
              Imagem, vídeo ou documento no cabeçalho:
            </strong>{' '}
            ainda não dá por aqui. A Meta exige que o arquivo de exemplo suba
            por uma rota própria dela antes de o modelo ser criado, e o gateway
            (OpenBSP) não expõe essa rota hoje. Enquanto isso: crie o modelo com
            mídia direto no Gerenciador da Meta — ele aparece nesta lista na
            próxima sincronização, com a imagem visível na prévia.
          </p>

          <Field
            label="Corpo"
            required
            error={errors.bodyText?.message}
            hint="Use “Inserir variável” em vez de digitar {{1}} à mão."
          >
            {(field) => (
              <div className="flex flex-col gap-2">
                <Textarea
                  {...field}
                  {...bodyField}
                  ref={mergeTextareaRefs(bodyField.ref, bodyRef)}
                  rows={5}
                  placeholder="Olá, seu pedido chegou na loja."
                />
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="self-start"
                  leadingIcon={
                    <Braces aria-hidden="true" className="h-4 w-4" />
                  }
                  onClick={insertVariable}
                >
                  Inserir variável
                </Button>
              </div>
            )}
          </Field>

          {variables.length === 0 ? null : (
            <fieldset className="flex flex-col gap-3 rounded-(--radius-md) border border-(--border-default) p-3">
              <legend className="px-1 text-(length:--text-sm) font-(--weight-medium) text-(--text-primary)">
                Variáveis
              </legend>

              {variables.map((variable) => (
                <div
                  key={variable.index}
                  className="grid grid-cols-[auto_1fr_1fr_auto] items-end gap-2"
                >
                  <span className="mb-2 rounded-(--radius-sm) bg-(--surface-sunken) px-2 py-1 text-(length:--text-xs) font-(--font-mono) text-(--text-muted)">
                    {`{{${String(variable.index)}}}`}
                  </span>

                  <Field label="Nome" required>
                    {(field) => (
                      <Input
                        {...field}
                        value={variable.name}
                        onChange={(event) => {
                          renameVariable(variable.index, event.target.value)
                        }}
                        placeholder="Nome do cliente"
                      />
                    )}
                  </Field>

                  <Field label="Exemplo">
                    {(field) => (
                      <Input
                        {...field}
                        value={variable.example}
                        onChange={(event) => {
                          exemplifyVariable(variable.index, event.target.value)
                        }}
                        placeholder="João"
                      />
                    )}
                  </Field>

                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label={`Remover variável ${String(variable.index)}`}
                    onClick={() => {
                      removeVariable(variable.index)
                    }}
                  >
                    <Trash2 aria-hidden="true" className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </fieldset>
          )}

          <Field
            label="Rodapé"
            error={errors.footerText?.message}
            hint="Opcional. Sem variáveis — a Meta não substitui ali."
          >
            {(field) => <Input {...field} {...footerField} />}
          </Field>

          {/*
            F066 — o editor de botões. Até aqui o formulário mandava
            `buttons: []` fixo enquanto a prévia já sabia desenhá-los.
          */}
          <fieldset className="flex flex-col gap-3 rounded-(--radius-md) border border-(--border-default) p-3">
            <legend className="px-1 text-(length:--text-sm) font-(--weight-medium) text-(--text-primary)">
              Botões
            </legend>

            {buttonDrafts.length === 0 ? (
              <p className="text-(length:--text-sm) text-(--text-muted)">
                Opcional. Até dez — a Meta decide, na análise, se a combinação
                que você escolheu é permitida.
              </p>
            ) : null}

            {buttonDrafts.map((draft, index) => (
              <div
                // A posição É a identidade aqui: um botão não tem id, e
                // remover reordena a lista inteira. `updateButton` sempre
                // reescreve por índice, então o React nunca precisa casar
                // linhas entre renders diferentes.
                key={index}
                className="grid grid-cols-[10rem_1fr_auto] items-end gap-2 sm:grid-cols-[10rem_1fr_1fr_auto]"
              >
                <Field label="Tipo" required>
                  {(field) => (
                    <Select
                      {...field}
                      value={draft.type}
                      onChange={(event) => {
                        updateButton(index, {
                          type: event.target.value as TemplateButtonType,
                        })
                      }}
                    >
                      {(
                        Object.keys(BUTTON_TYPE_LABEL) as TemplateButtonType[]
                      ).map((type) => (
                        <option key={type} value={type}>
                          {BUTTON_TYPE_LABEL[type]}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>

                <Field label="Texto do botão" required>
                  {(field) => (
                    <Input
                      {...field}
                      value={draft.text}
                      maxLength={25}
                      placeholder="Ver meu pedido"
                      onChange={(event) => {
                        updateButton(index, { text: event.target.value })
                      }}
                    />
                  )}
                </Field>

                {draft.type === 'URL' ? (
                  <Field label="Endereço" required>
                    {(field) => (
                      <Input
                        {...field}
                        type="url"
                        value={draft.url}
                        placeholder="https://sualoja.com.br/pedido"
                        onChange={(event) => {
                          updateButton(index, { url: event.target.value })
                        }}
                      />
                    )}
                  </Field>
                ) : null}

                {draft.type === 'PHONE_NUMBER' ? (
                  <Field label="Telefone" required>
                    {(field) => (
                      <Input
                        {...field}
                        type="tel"
                        value={draft.phoneNumber}
                        placeholder="+5511999998888"
                        onChange={(event) => {
                          updateButton(index, {
                            phoneNumber: event.target.value,
                          })
                        }}
                      />
                    )}
                  </Field>
                ) : null}

                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={`Remover botão ${String(index + 1)}`}
                  onClick={() => {
                    removeButton(index)
                  }}
                >
                  <Trash2 aria-hidden="true" className="h-4 w-4" />
                </Button>
              </div>
            ))}

            {buttonDrafts.length >= 10 ? null : (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="self-start"
                leadingIcon={<Plus aria-hidden="true" className="h-4 w-4" />}
                onClick={addButton}
              >
                Adicionar botão
              </Button>
            )}
          </fieldset>

          {formError === null ? null : (
            <p role="alert" className="text-(length:--text-sm) text-(--danger)">
              {formError}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <Button asChild variant="ghost">
              <Link to="/atendimento-modelos">Cancelar</Link>
            </Button>
            <Button type="submit" variant="primary" loading={isSubmitting}>
              Submeter à Meta
            </Button>
          </div>
        </form>

        {/*
          Abaixo de `lg` a prévia vem PRIMEIRO e gruda no topo: era o único
          lugar da tela em que ela sumia — empurrada para depois de um
          formulário de dez campos, justamente na largura em que o texto que
          se digita cabe menos na cabeça. `order-first` sem `sticky` só
          trocaria um sumiço por outro (ela sairia da vista na primeira
          rolagem). `lg:top-4` porque `top-0` colava a prévia no cabeçalho
          da página.
        */}
        <aside className="sticky top-0 z-10 order-first self-start bg-(--surface-base) pb-2 lg:order-none lg:top-4 lg:pb-0">
          <WhatsAppPreview
            rendered={rendered}
            at={new Date()}
            slotLabels={slotLabels}
          />
        </aside>
      </div>

      <ConfirmDialog
        open={confirming !== null}
        onOpenChange={(open) => {
          if (!open) setConfirming(null)
        }}
        title="Submeter este modelo à Meta?"
        description="A análise leva alguns dias e não dá para cancelar. Para mudar qualquer palavra depois, o caminho é criar outro modelo — este continuará ocupando um nome dentro da sua conta."
        confirmLabel="Submeter"
        loading={save.isPending}
        onConfirm={() => {
          const values = confirming
          setConfirming(null)
          if (values !== null) save.mutate(values)
        }}
      />
    </Page>
  )
}
